import type { FieldExplainerContent } from './types';

export type BudgetPlannerExplainerContext = {
  input: {
    salaryIncome: number;
    sideIncome: number;
    housing: number;
    utilities: number;
    groceries: number;
    transportation: number;
    insurance: number;
    entertainment: number;
    dining: number;
    savings: number;
  };
  result: {
    totalIncome: number;
    totalExpenses: number;
    remaining: number;
    needsTotal: number;
    wantsTotal: number;
    savingsTotal: number;
    needsPercent: number;
    wantsPercent: number;
    savingsPercent: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

export function getBudgetPlannerFieldExplainers(ctx: BudgetPlannerExplainerContext): FieldExplainerContent[] {
  const { salaryIncome, sideIncome, housing, utilities, groceries, transportation, insurance, entertainment, dining, savings } =
    ctx.input;
  const { totalIncome, totalExpenses, remaining, needsTotal, wantsTotal, savingsTotal, needsPercent, wantsPercent, savingsPercent } =
    ctx.result;

  const needsList = `${usd.format(housing)} + ${usd.format(utilities)} + ${usd.format(groceries)} + ${usd.format(transportation)} + ${usd.format(insurance)}\n  = ${usd.format(needsTotal)}`;
  const wantsList = `${usd.format(entertainment)} + ${usd.format(dining)}\n  = ${usd.format(wantsTotal)}`;
  const allocatedPercent = totalIncome > 0 ? ((needsTotal + wantsTotal + savingsTotal) / totalIncome) * 100 : 0;
  const percentSum = needsPercent + wantsPercent + savingsPercent;

  return [
    {
      id: 'salaryIncome',
      kind: 'input',
      label: 'Salary income',
      plain:
        'Your regular paycheck, entered as a flat monthly number. The planner treats it as already-in-hand cash — nothing is withheld for tax here, so use your actual take-home deposit, not your gross salary.',
      formula: `salary = ${usd.format(salaryIncome)}   (your input)\ncounted directly into total income`,
      feeds: 'Total income, remaining cash flow, and the denominator behind all three needs/wants/savings percentages.'
    },
    {
      id: 'sideIncome',
      kind: 'input',
      label: 'Side income',
      plain:
        'Anything outside the regular paycheck — freelance work, a side business, rental income. It is added to salary with the same treatment: entered as net cash, no tax modeled.',
      formula: `side = ${usd.format(sideIncome)}   (your input)\ntotalIncome = ${usd.format(salaryIncome)} + ${usd.format(sideIncome)}\n  = ${usd.format(totalIncome)}`,
      feeds: 'Total income, alongside salary income.',
      assumption:
        salaryIncome > 0 && sideIncome === 0
          ? 'Set to $0, so the plan currently runs on salary alone.'
          : undefined
    },
    {
      id: 'housing',
      kind: 'input',
      label: 'Housing',
      plain: 'Rent or mortgage payment. This is one of the five categories the planner hard-codes as a "need."',
      formula: `housing (needs) = ${usd.format(housing)}\nneedsTotal = ${needsList}`,
      feeds: 'The needs total, the needs bar on the category chart, and the needs percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'utilities',
      kind: 'input',
      label: 'Utilities',
      plain: 'Electricity, gas, water, internet — the recurring bills that keep the home running. Categorized as a "need."',
      formula: `utilities (needs) = ${usd.format(utilities)}\nneedsTotal = ${needsList}`,
      feeds: 'The needs total, the needs bar on the category chart, and the needs percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'groceries',
      kind: 'input',
      label: 'Groceries',
      plain: 'Food bought to cook and eat at home, kept separate from restaurant spending. Categorized as a "need."',
      formula: `groceries (needs) = ${usd.format(groceries)}\nneedsTotal = ${needsList}`,
      feeds: 'The needs total, the needs bar on the category chart, and the needs percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'transportation',
      kind: 'input',
      label: 'Transportation',
      plain: 'Car payment, gas, insurance premiums for the vehicle, transit passes — however you get to work. Categorized as a "need."',
      formula: `transportation (needs) = ${usd.format(transportation)}\nneedsTotal = ${needsList}`,
      feeds: 'The needs total, the needs bar on the category chart, and the needs percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'insurance',
      kind: 'input',
      label: 'Insurance',
      plain: 'Health, life, or renters/homeowners premiums — coverage you pay for whether or not you use it. Categorized as a "need."',
      formula: `insurance (needs) = ${usd.format(insurance)}\nneedsTotal = ${needsList}`,
      feeds: 'The needs total, the needs bar on the category chart, and the needs percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'entertainment',
      kind: 'input',
      label: 'Entertainment',
      plain: 'Streaming, hobbies, nights out, subscriptions bought for fun rather than function. Categorized as a "want."',
      formula: `entertainment (wants) = ${usd.format(entertainment)}\nwantsTotal = ${wantsList}`,
      feeds: 'The wants total, the wants bar on the category chart, and the wants percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'dining',
      kind: 'input',
      label: 'Dining',
      plain: 'Restaurants, takeout, coffee runs — food you didn’t cook yourself. Categorized as a "want," separately from the groceries need.',
      formula: `dining (wants) = ${usd.format(dining)}\nwantsTotal = ${wantsList}`,
      feeds: 'The wants total, the wants bar on the category chart, and the wants percentage in the 50/30/20 snapshot.'
    },
    {
      id: 'savings',
      kind: 'input',
      label: 'Savings',
      plain:
        'Money you are deliberately setting aside this month. The planner counts it as one of the eight budget categories, not as leftover cash — it is subtracted from income the same way rent is.',
      formula: `savings = ${usd.format(savings)}\nsavingsTotal = ${usd.format(savingsTotal)}   (savings is the only category of this type)`,
      feeds: 'Total expenses, remaining cash flow, and the savings percentage in the 50/30/20 snapshot.',
      assumption:
        'This is treated as spent money for budgeting purposes. Any income left over after this line is reported separately, in "Remaining cash flow," and is not added to this savings figure.'
    },
    {
      id: 'totalIncome',
      kind: 'result',
      label: 'Total income',
      plain: 'Every income field added together, with no tax or deduction modeling.',
      formula: `${usd.format(salaryIncome)} + ${usd.format(sideIncome)}\n  = ${usd.format(totalIncome)}`,
      feeds: 'The denominator for every percentage on the page, and one side of the remaining cash-flow subtraction.'
    },
    {
      id: 'totalExpenses',
      kind: 'result',
      label: 'Total expenses',
      plain:
        'All eight category amounts added together. Because "Savings" is one of those eight categories, this total already includes the money you plan to save, not just money you plan to spend.',
      formula: `${usd.format(housing)} + ${usd.format(utilities)} + ${usd.format(groceries)} + ${usd.format(transportation)} + ${usd.format(insurance)} + ${usd.format(entertainment)} + ${usd.format(dining)} + ${usd.format(savings)}\n  = ${usd.format(totalExpenses)}`,
      feeds: 'Remaining cash flow, and the category table and bar chart directly.'
    },
    {
      id: 'remaining',
      kind: 'result',
      label: 'Remaining cash flow',
      plain:
        remaining >= 0
          ? 'What is left over after every category, including savings, has been subtracted from income. A positive number here is cash that has not been assigned anywhere yet.'
          : 'Income minus all eight categories, including savings. A negative number means the categories you entered add up to more than you bring in.',
      formula: `${usd.format(totalIncome)} − ${usd.format(totalExpenses)}\n  = ${usd.format(remaining)}`,
      feeds: 'Nothing downstream on this page — it is a standalone check figure.',
      assumption:
        remaining >= 0
          ? 'This unassigned amount is not drawn as a slice on the allocation pie chart, which shows only needs, wants, and savings.'
          : 'This overspend is not drawn on the allocation pie chart, which shows only needs, wants, and savings — the shortfall exists only on this card.'
    },
    {
      id: 'allocationMix',
      kind: 'result',
      label: '50/30/20 snapshot',
      plain:
        'Each of needs, wants, and savings measured as a percentage of total income. The three are rounded together so they add back up to the true combined share of income they represent, instead of each being rounded on its own and drifting off by a point.',
      formula:
        totalIncome > 0
          ? `needs = ${usd.format(needsTotal)} / ${usd.format(totalIncome)} = ${needsPercent}%\nwants = ${usd.format(wantsTotal)} / ${usd.format(totalIncome)} = ${wantsPercent}%\nsavings = ${usd.format(savingsTotal)} / ${usd.format(totalIncome)} = ${savingsPercent}%\n\n${needsPercent}% + ${wantsPercent}% + ${savingsPercent}% = ${percentSum}%  (${allocatedPercent.toFixed(1)}% of income is assigned to a category)`
          : `total income is $0, so there is nothing\nto measure the categories against —\nevery percentage reads 0%`,
      feeds: 'The category bar chart colors, the allocation donut chart, and the headline "needs/wants/savings" figure shown at the top of the results.',
      assumption:
        totalIncome > 0 && percentSum < 100 && remaining > 0
          ? `These percentages are each measured against total income, not against total expenses, so they only sum to 100% when every dollar of income is assigned to a category. Right now ${usd.format(remaining)} of income is unassigned, so they sum to ${percentSum}% instead of 100%.`
          : undefined
    }
  ];
}
